<template>
  <div class="page__main--640 typography">
    <h1 class="m-b-m">Typography</h1>
    <p class="m-b-xl"><fds-text type="lead">Downloadable fonts and text elements scaled for different screen sizes.</fds-text></p>
    <section class="typography__fonts">
      <h2 class="m-b-s">Fonts families</h2>
      <p>There're two font families in FDS. Raleway for headings and <a href="https://github.com/IBM/plex/tree/master/packages/plex-sans-variable" target="_blank" rel="noreferrer noopener">IBM Plex Sans</a> for everything else. As a backup font family, we use Arial. For web-based solutions, please use the Variable Font version of IBM Plex Sans for optimal performance.</p>
      <div class="typography__fonts__blocks">
        <article class="content-block" v-for="font in fonts" :key="font.name">
          <div class="meta">
            <strong class="meta__heading fds-label" :style="{fontFamily: font.font, fontWeight: font.wght}">{{font.name}}</strong>
            <a :href="font.url" target="_blank" rel="noreferrer noopener">{{downloadLabel(font.url)}}</a>
          </div>
        </article>
      </div>
    </section>

    <section>
      <h2 class="m-t-xl m-b-s">Text elements</h2>
      <p class="m-b-xl">It's a good idea to scale up text for larger screens. For inspiration, see how these text elements are scaled for small screens to extra large screens.</p>
      <div>
        <fds-tabs class="m-b-xl" block>
          <fds-tabs-item v-for="tab in tabs" :key="tab.name" :active="tab.name === selectedTab">
            <button @click="handleTab(tab.name)">{{tab.title}}</button>
          </fds-tabs-item>
        </fds-tabs>
      </div>

      <article class="content-block typography__element" v-for="(el) in filteredElements" :key="el.id">
        <component class="element" :is="el.tag" :id="el.id" :class="el.class" ref="elList">
          {{el.text}}
        </component>
        <ul class="typography__element__list">
          <li v-for="(val, key) in el.css" :key="`${el.id}-${key}`">
            <span class="key">{{key}}</span>:
            <span class="val">{{val}}</span>;
          </li>
        </ul>
      </article>
    </section>
  </div>
</template>

<script>
export default {
  name: 'typography',
  data () {
    return {
      fonts: [
        {
          name: 'Raleway-Extra-Bold',
          url: 'https://fonts.google.com/specimen/Raleway',
          font: 'Raleway',
          wght: '800'
        },
        {
          name: 'IBM-Plex-Sans-Bold',
          url: 'https://fonts.google.com/specimen/IBM+Plex+Sans',
          font: 'IBM-Plex-Sans',
          wght: '700'
        },
        {
          name: 'IBM-Plex-Sans-Regular',
          url: 'https://fonts.google.com/specimen/IBM+Plex+Sans',
          font: 'IBM-Plex-Sans',
          wght: '400'
        },
        {
          name: 'IBM-Plex-Sans-Light',
          url: 'https://fonts.google.com/specimen/IBM+Plex+Sans',
          font: 'IBM-Plex-Sans',
          wght: '300'
        }
      ],
      selectedFont: 'IBM-Plex-Sans-Regular',
      fontSize: 1,
      tabs: [
        {
          title: 'Small',
          name: 'small'
        },
        {
          title: 'Medium',
          name: 'medium'
        },
        {
          title: 'Large',
          name: 'large'
        },
        {
          title: 'Extra large',
          name: 'xlarge'
        }
      ],
      selectedTab: 'large',

      elements: [
        // H1
        { tag: 'h1', xlarge: true, large: false, medium: false, small: false, id: 'h1-xl', class: 'h1 h1--xl', text: 'H1', css: {} },
        { tag: 'h1', xlarge: false, large: true, medium: false, small: false, id: 'h1-l', class: 'h1 h1--l', text: 'H1', css: {} },
        { tag: 'h1', xlarge: false, large: false, medium: true, small: false, id: 'h1-m', class: 'h1 h1--m', text: 'H1', css: {} },
        { tag: 'h1', xlarge: false, large: false, medium: false, small: true, id: 'h1-s', class: 'h1 h1--s', text: 'H1', css: {} },

        // H2
        { tag: 'h2', xlarge: true, large: false, medium: false, small: false, id: 'h2-xl', class: 'h2 h2--xl', text: 'H2', css: {} },
        { tag: 'h2', xlarge: false, large: true, medium: false, small: false, id: 'h2-l', class: 'h2 h2--l', text: 'H2', css: {} },
        { tag: 'h2', xlarge: false, large: false, medium: true, small: false, id: 'h2-m', class: 'h2 h2--m', text: 'H2', css: {} },
        { tag: 'h2', xlarge: false, large: false, medium: false, small: true, id: 'h2-s', class: 'h2 h2--s', text: 'H2', css: {} },

        // H3
        { tag: 'h3', xlarge: true, large: false, medium: false, small: false, id: 'h3-xl', class: 'h3 h3--xl', text: 'H3', css: {} },
        { tag: 'h3', xlarge: false, large: true, medium: false, small: false, id: 'h3-l', class: 'h3 h3--l', text: 'H3', css: {} },
        { tag: 'h3', xlarge: false, large: false, medium: true, small: false, id: 'h3-m', class: 'h3 h3--m', text: 'H3', css: {} },
        { tag: 'h3', xlarge: false, large: false, medium: false, small: true, id: 'h3-s', class: 'h3 h3--s', text: 'H3', css: {} },

        // P
        { tag: 'p', xlarge: true, large: false, medium: false, small: false, id: 'p-xl', class: 'p p--xl', text: 'Body Text', css: {} },
        { tag: 'p', xlarge: false, large: true, medium: false, small: false, id: 'p-l', class: 'p p--l', text: 'Body Text', css: {} },
        { tag: 'p', xlarge: false, large: false, medium: true, small: true, id: 'p-s', class: 'p p--s', text: 'Body Text', css: {} },

        // Lead
        { tag: 'p', xlarge: true, large: false, medium: false, small: false, id: 'lead-xl', class: 'fds-lead lead lead--xl', text: 'Lead Text', css: {} },
        { tag: 'p', xlarge: false, large: true, medium: false, small: false, id: 'lead-l', class: 'fds-lead lead lead--l', text: 'Lead Text', css: {} },
        { tag: 'p', xlarge: false, large: false, medium: true, small: true, id: 'lead-s', class: 'fds-lead lead lead--s', text: 'Lead Text', css: {} },

        // Meta
        { tag: 'p', xlarge: true, large: false, medium: false, small: false, id: 'meta-xl', class: 'fds-meta meta- meta--xl', text: 'Meta Text', css: {} },
        { tag: 'p', xlarge: false, large: true, medium: false, small: false, id: 'meta-l', class: 'fds-meta meta- meta--l', text: 'Meta Text', css: {} },
        { tag: 'p', xlarge: false, large: false, medium: true, small: true, id: 'meta-s', class: 'fds-meta meta- meta--s', text: 'Meta Text', css: {} },

        // Label
        { tag: 'label', xlarge: true, large: false, medium: false, small: false, id: 'list-label-xl', class: 'fds-label label label--xl', text: 'Label', css: {} },
        { tag: 'label', xlarge: false, large: true, medium: false, small: false, id: 'list-label-l', class: 'fds-label label label--l', text: 'Label', css: {} },
        { tag: 'label', xlarge: false, large: false, medium: true, small: true, id: 'list-label-s', class: 'fds-label label label--s', text: 'Label', css: {} }
      ]

    }
  },
  computed: {
    filteredElements () {
      const target = this.selectedTab
      // Only show the large or small variation
      const elements = this.elements.filter(item => {
        return item[target]
      })
      return elements
    }
  },
  mounted () {
    this.setComputedStyles()
  },
  methods: {
    handleTab (tab) {
      this.selectedTab = tab
      const self = this
      setTimeout(function () {
        self.setComputedStyles()
      }, 0)
    },
    setComputedStyles () {
      const list = this.$refs.elList
      for (let ref = 0; ref < list.length; ref++) {
        for (let el = 0; el < this.filteredElements.length; el++) {
          if (list[ref].id === this.filteredElements[el].id) {
            const element = list[ref]
            const object = this.filteredElements[el]
            const styles = this.compStyles(element)
            object.css = styles
          }
        }
      }
    },
    getRem (px) {
      let unit = parseInt(px.substring(0, 2))
      unit = (unit / 16).toString()
      return `${unit}rem`
    },
    getEm (size, spacing) {
      if (spacing === 'normal') {
        return '0'
      }
      const sizeUnit = parseFloat(size.substring(size.length, -2))
      const spacingUnit = parseFloat(spacing.substring(spacing.length, -2))
      const unitLess = spacingUnit / sizeUnit
      return `${unitLess}em`
    },
    getUnitLess (size, spacing) {
      if (spacing === 'normal') {
        return '0'
      }
      const sizeUnit = parseFloat(size.substring(size.length, -2))
      const spacingUnit = parseFloat(spacing.substring(spacing.length, -2))
      const unitLess = spacingUnit / sizeUnit
      return `${unitLess}`
    },
    compStyles (el) {
      const obj = {
        color: getComputedStyle(el).color,
        'font-size': this.getRem(getComputedStyle(el).fontSize),
        'font-family': getComputedStyle(el).fontFamily,
        'line-height': this.getUnitLess(getComputedStyle(el).fontSize, getComputedStyle(el).lineHeight),
        'letter-spacing': this.getEm(getComputedStyle(el).fontSize, getComputedStyle(el).letterSpacing),
        'font-weight': getComputedStyle(el).fontWeight
      }
      // Fix floating point value bug for rgba
      if (obj.color.split('').splice(0, 4).join('') === 'rgba') {
        const r = Number(obj.color.split(',')[0].replace('rgba(', ''))
        const g = Number(obj.color.split(',')[1])
        const b = Number(obj.color.split(',')[2])
        const alpha = Number(obj.color.split(',')[3].replace(')', ''))
        const alphaR = Math.round((alpha + Number.EPSILON) * 100) / 100
        obj.color = `rgba(${r}, ${g}, ${b}, ${alphaR})`
      }
      return obj
    },
    downloadLabel (url) {
      const target = new URL(url)
      switch (target.host) {
        case 'fonts.google.com':
          return 'View at Google Fonts'
        case 'v-fonts.com':
          return 'View at Variable Fonts'
        case 'github.com':
          return 'View at GitHub'
        default:
          return 'Download'
      }
    }
  }
}
</script>
<style lang="scss">
.typography {
  &__fonts {
    &__blocks {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: set-gutter('m');
      margin-bottom: 0;
      .content-block {
        padding: set-gutter('xl');
        margin-bottom: 0;
        & .meta {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          &__heading {
            margin-bottom: 0;
            font-size: set-size('l');
          }
        }
      }
    }
  }
  &__element {
    padding: 0;
    display: flex;
    align-items: center;
    flex-direction: column;
    @media all and (min-width: set-bp('s')) {
      flex-direction: row;
    }
    .element {
      margin: 0;
      padding: set-gutter('l');
      text-align: center;
      white-space: nowrap;
      @media all and (min-width: set-bp('s')) {
        width: 140px;
      }
    }
    &__list {
      flex: 1;
      list-style: none;
      margin: 0;
      padding: 0;
      // To match the color on components code-view
      background-color: #272c35;
      padding: set-gutter('s');
      border-radius: 0 0 set-radius('m') set-radius('m');
      width: 100%;
      overflow: auto;
      @media all and (min-width: set-bp('s')) {
        width: auto;
        border-radius: 0 set-radius('m') set-radius('m') 0;
      }
      > li {
        margin: 0;
        width: 100%;
        white-space: nowrap;
        line-height: 1.1rem;
        color: #999;
        font-family: set-family('monospace');
        font-size: set-size('s');
      }
      .key, .val {
        display: inline;
        font-size: set-size('m');
        font-family: set-family('monospace');
        font-size: set-size('s');
      }
      .key {
        color: skyblue;
      }
      .val {
        color:white;
      }
    }
  }
}

.h1 {
  &--s {
    font-size: set-size('xxl');
    letter-spacing: set-letter-spacing('s');
  }
  &--m {
    font-size: set-size('4xl');
    letter-spacing: set-letter-spacing('s');
  }
  &--l {
    font-size: set-size('5xl');
    letter-spacing: set-letter-spacing('s');
  }
  &--xl {
    font-size: set-size('6xl');
    letter-spacing: set-letter-spacing('s');
  }
}

.h2 {
  &--s {
    font-size: set-size('xl');
  }
  &--m {
    font-size: set-size('3xl');
  }
  &--l {
    font-size: set-size('4xl');
  }
  &--xl {
    font-size: set-size('5xl');
    letter-spacing: set-letter-spacing('s');
  }
}

.h3 {
  &--s {
    font-size: set-size('l');
  }
  &--m {
    font-size: set-size('xl');
  }
  &--l {
    font-size: set-size('xxl');
  }
  &--xl {
    font-size: set-size('3xl');
  }
}

.p, .lead, .meta, .label {
  letter-spacing: set-letter-spacing('l');
}

.p, .meta, .label {
  &--s {
    font-size: set-size('m');
  }
  &--l {
    font-size: set-size('l');
  }
  &--xl {
    font-size: set-size('xl');
  }
}

.lead {
  &--s {
    font-size: set-size('l');
  }
  &--l {
    font-size: set-size('xl');
  }
  &--xl {
    font-size: set-size('xxl');
  }
}
</style>
